"use client";

import { useState } from "react";

import animationData1 from "public/assets/homePage/generateSection/1st.json";
import animationData2 from "public/assets/homePage/generateSection/2nd.json";
import animationData3 from "public/assets/homePage/generateSection/3rd.json";
import FeatureCard from "./FeatureCards";

const cards = [
  {
    badgeIndex: 0,
    title: "Creative intelligence built into every campaign.",
    description:
      "AI analyzes hooks, visuals, messaging, and formats to reveal which creative patterns actually drive performance.",
    animation: animationData1,
    caption: "Creative analysis",
  },
  {
    badgeIndex: 1,
    title: "Generate hooks, headlines, and copy that convert.",
    description:
      "Generate conversion-focused headlines, hooks, CTAs, and campaign messaging tailored to your brand voice.",
    animation: animationData2,
    caption: "AI copywriter",
  },
  {
    badgeIndex: 2,
    title: "Generate image and video ads at production speed.",
    description:
      "Create high-performing ads from prompts, products, or URLs  ready for every campaign format.",
    animation: animationData3,
    caption: "Image & video AI",
  },
];

const GenerateSection = () => {
  const [generateInput, setGenerateInput] = useState("");
  return (
    <section
      aria-labelledby="section-heading "
      className="bg-[#F5F5F7] pt-20 pb-8 sm:pt-24 sm:pb-24 lg:pt-28 lg:pb-28   px-6 md:px-[40px] xl:px-20"
    >
      {/* ── Header ── */}
      <header className="text-center space-y-4 lg:max-w-4xl  2xl:max-w-full mx-auto">
        <h2 id="section-heading" className="subheading ">
          All Your Ad Creatives. One <br className="lg:hidden" />
          <span className="text-[#3232A4]"> AI System.</span>
        </h2>

        <p className="body-text max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] 2xl:max-w-[900px] 3xl:max-w-[1000px] mx-auto ">
          Generate image ads, video ads, hooks, copy, and campaign variants from
          a single workflow powered by performance intelligence built into every
          step.
        </p>
      </header>

      {/* ── Search / Generate bar ── */}
      <div
        role="search"
        aria-label="Create ad"
        className="relative w-full max-w-[672px] mt-6 sm:mt-12 sm:mb-[50px]  mb-[30px]  lg:mb-[72px] mx-auto"
      >
        <label htmlFor="ad-prompt" className="sr-only  ">
          Describe what you want to create
        </label>

        <span className="absolute left-3 top-1/2 -translate-y-1/2">
          <span className="block">
            <svg
              width="38"
              height="38"
              viewBox="0 0 38 38"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M23.659 16.0433L22.5069 13.5087L19.9723 12.3566L22.5069 11.2045L23.659 8.66992L24.8111 11.2045L27.3457 12.3566L24.8111 13.5087L23.659 16.0433ZM23.659 28.9467L22.5069 26.4121L19.9723 25.26L22.5069 24.1079L23.659 21.5733L24.8111 24.1079L27.3457 25.26L24.8111 26.4121L23.659 28.9467ZM14.4422 26.1817L12.138 21.1125L7.06885 18.8083L12.138 16.5041L14.4422 11.4349L16.7464 16.5041L21.8156 18.8083L16.7464 21.1125L14.4422 26.1817Z"
                fill="url(#paint0_linear_2012_2602)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2012_2602"
                  x1="17.2073"
                  y1="8.66992"
                  x2="17.2073"
                  y2="28.9467"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#0000CC" />
                  <stop offset="1" stopColor="#3232A4" />
                </linearGradient>
              </defs>
            </svg>
          </span>
        </span>

        <div className="p-[1.5px] rounded-full bg-gradient-to-b from-[#0000CC]  to-[#3232A4] shadow-sm focus-within:ring-2 focus-within:ring-[#3232A4]/20 transition-all">
          <input
            id="ad-prompt"
            type="search"
            value={generateInput}
            onChange={(e) => setGenerateInput(e.target.value)}
            placeholder="Describe what you want to create…"
            className="w-full rounded-full bg-white py-3 lg:py-5 pl-14 pr-36 outline-none text-[14px] lg:text-[16px] text-[#0E0B1A] placeholder:text-[#9B97A8] placeholder:text-[14px] lg:placeholder:text-[16px]"
          />
        </div>

        <span className="absolute right-[110px] lg:right-40 top-1/2 -translate-y-1/2">
          <span className="block">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.73145 15.2275L9.28418 15.1875C6.27092 14.9155 3.9082 12.3843 3.9082 9.31348V8.02148C3.90824 7.9951 3.91839 7.97614 3.93164 7.96289C3.94489 7.94964 3.96385 7.93949 3.99023 7.93945C4.01659 7.93945 4.03556 7.9497 4.04883 7.96289C4.06208 7.97614 4.07223 7.9951 4.07227 8.02148V9.32227C4.07248 12.4878 6.64695 15.0624 9.8125 15.0625C12.9782 15.0625 15.5535 12.4879 15.5537 9.32227V8.03027C15.5537 8.00381 15.5629 7.98398 15.5762 7.9707C15.5894 7.95753 15.6085 7.94833 15.6348 7.94824C15.672 7.94824 15.7152 7.98201 15.7168 8.03418V9.31348C15.7168 12.385 13.3455 14.9234 10.3418 15.1875L9.89453 15.2266V17.418C9.89444 17.4441 9.8852 17.4634 9.87207 17.4766C9.85879 17.4898 9.83896 17.499 9.8125 17.499C9.78622 17.499 9.76713 17.4898 9.75391 17.4766C9.74069 17.4634 9.73154 17.4442 9.73145 17.418V15.2275Z"
                fill="#7C7C7C"
                stroke="#7C7C7C"
                strokeWidth="0.9813"
              />
              <path
                d="M9.81299 2.12598C11.5373 2.12598 12.9369 3.52576 12.937 5.25V9.4375C12.9368 11.1616 11.5372 12.5605 9.81299 12.5605C8.08889 12.5604 6.6892 11.1616 6.68896 9.4375V5.25C6.68907 3.52582 8.08881 2.12608 9.81299 2.12598ZM11.1997 4.66699C10.3619 4.36156 9.4431 4.34211 8.59521 4.60938L8.43994 4.66211C7.9254 4.83362 7.66548 5.40304 7.84814 5.90137C7.96245 6.21311 8.22483 6.43325 8.52881 6.50879C8.28866 6.74461 8.17627 7.10078 8.27881 7.45508C8.41628 7.95833 8.93295 8.27601 9.46338 8.12695H9.46436C9.69604 8.06167 9.93873 8.06163 10.1704 8.12695L10.189 8.13184L10.2075 8.13574C10.2395 8.14214 10.3285 8.1611 10.4263 8.16113C10.8571 8.16113 11.2421 7.87658 11.3579 7.44629L11.3599 7.44141C11.4479 7.09989 11.3422 6.74063 11.1011 6.50293C11.3991 6.43001 11.6582 6.22122 11.7769 5.90527L11.7788 5.90137C11.9653 5.39241 11.6908 4.84679 11.2007 4.66699H11.1997Z"
                fill="#7C7C7C"
                stroke="#7C7C7C"
                strokeWidth="0.9813"
              />
            </svg>
          </span>
        </span>
        <span className="absolute right-2  lg:right-4 top-1/2 -translate-y-1/2">
          <a
            href="https://app.imagive.ai?redirect=/users/white-chat-board"
            className={` ${!generateInput ? "pointer-events-none" : ""} btn-gradient rounded-full flex items-center gap-1  font-medium transition-opacity duration-150 hover:opacity-90 active:opacity-80`}
            target="_blank"
          >
            <svg
              width="15"
              height="13"
              viewBox="0 0 15 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.0693 4.46861L8.76571 3.20394L6.87836 3.10265L8.25499 1.80758L8.43502 0.000281012L9.73862 1.26495L11.626 1.36623L10.2493 2.66131L10.0693 4.46861ZM12.9293 12.2882L11.6257 11.0235L9.73835 10.9222L11.115 9.62716L11.295 7.81985L12.5986 9.08452L14.4859 9.18581L13.1093 10.4809L12.9293 12.2882ZM6.38194 12.7831L3.77475 10.2537L6.20878e-05 10.0512L2.75332 7.46103L3.11338 3.84643L5.72057 6.37577L9.49526 6.57834L6.74199 9.16848L6.38194 12.7831Z"
                fill="white"
              />
            </svg>
            Generate
          </a>
        </span>
      </div>

      {/* ── Feature cards ── */}
      <div
        role="list"
        aria-label="Features"
        className="grid grid-cols-1 gap-4 lg:gap-6 md:grid-cols-3 place-items-center mx-auto max-w-[1000px] xl:max-w-[1240px] "
        // className="flex lg:flex-row flex-col items-center justify-center gap-4 lg:gap-6"
      >
        {cards.map((card, i) => (
          <FeatureCard key={i} card={card} />
        ))}
      </div>
    </section>
  );
};

export default GenerateSection;
